import first from "lodash/first";
import type { FunctionComponentWithRef, ReactNode } from "react";
import { graphql, useFragment } from "react-relay";
import { FollowType } from "scmp-sdk";

import { useCurrentPageType } from "shared/hooks";
import { PageType } from "shared/lib/page-type";
import { notEmpty } from "shared/lib/utils";

import { checkHasLiveTag } from "scmp-app/components/article/article-render/helpers";
import { checkIsPlusArticle } from "scmp-app/components/article/article-render/plus/helpers";
import { withHighlightedHeadline } from "scmp-app/components/content/content-item-render/highlight/hoc";
import { LinkHighlightArea } from "scmp-app/components/content/content-item-render/highlight/styles";
import { useContentItemProviders } from "scmp-app/components/content/content-item-render/hooks";
import type { ContentItemRenderVariantProps } from "scmp-app/components/content/content-item-render/types";
import { getContentSponsor } from "scmp-app/components/content/content-sponsor-tag/helpers";
import { usePgType, usePlusQueryParameters } from "scmp-app/components/content/helpers";
import { PlusFlag } from "scmp-app/components/plus/plus-flag";
import type { sectionTopLeadPrimaryContentItemContent$key } from "scmp-app/queries/__generated__/sectionTopLeadPrimaryContentItemContent.graphql";

import {
  ActionBar,
  Container,
  Header,
  Headline,
  LeadingImage,
  LiveTagContainer,
  Primary,
  Rest,
  Section,
  SectionContainer,
  StyledContentCoverImage,
  StyledEntityLink,
  StyledHomeFollowButton,
  Summary,
  Topic,
  TopicContainer,
  TopicLabel,
} from "./styles";

export type Props = ContentItemRenderVariantProps & {
  children?: ReactNode;
  currentTopicReference?: string;
  preferTopicLabel?: boolean;
  reference: sectionTopLeadPrimaryContentItemContent$key;
  withBookmark?: boolean;
  withComment?: boolean;
  withLiveTag?: boolean;
  withSection?: boolean;
  withTopic?: boolean;
  withVideoOverlay?: boolean;
};

const Component: FunctionComponentWithRef<Props, HTMLDivElement> = ({
  children,
  className,
  currentTopicReference,
  customQueryParameters,
  onClick,
  preferTopicLabel,
  ref,
  reference,
  topicLinkVariant,
  withBookmark = false,
  withComment = false,
  withLiveTag = false,
  withSection = false,
  withTopic = false,
  withVideoOverlay = false,
}) => {
  const content = useFragment(
    graphql`
      fragment sectionTopLeadPrimaryContentItemContent on Content {
        entityId
        urlAlias
        topics {
          ...followButtonBase
        }
        ...entityLink
        ...contentCoverImageContent @arguments(withLandscape250x99: true, withSize540x360: true)
        ...helpersCheckIsPlusArticle
        ...helpersCheckHasLiveTagArticle
        ...helpersUseContentSponsorContent
        ...hooksContentItemProvidersContent
          @arguments(
            withSections: true
            withTopicLink: true
            withLiveTag: true
            withHeadline: true
            withSummary: true
            withSubHeadline: true
            withActionBar: true
            withSponsorTag: true
            withVideoDurationOverlay: true
          )
      }
    `,
    reference,
  );
  const providers = useContentItemProviders(content, topicLinkVariant);

  const contentSponsor = getContentSponsor(content);
  const isSponsorArticle = notEmpty(contentSponsor?.name);
  const topic = first(content?.topics ?? []);
  const currentPageType = useCurrentPageType();

  const hasLiveTag = checkHasLiveTag(content);
  const isShowLiveTag = hasLiveTag && withLiveTag;
  const isPlusArticle = checkIsPlusArticle(content);

  const pgtype = usePgType();
  const plusQueryParameters = usePlusQueryParameters(content);

  const renderHeader = () => {
    switch (true) {
      case isShowLiveTag:
        return <LiveTagContainer>{providers.liveTag()}</LiveTagContainer>;
      case withTopic:
        return preferTopicLabel ? (
          <TopicLabel>
            {providers.topicLink?.({ currentTopicReference, preferShortName: true })}
          </TopicLabel>
        ) : (
          <TopicContainer>
            <Topic>{providers.topicLink?.()}</Topic>
            {topic && (
              <StyledHomeFollowButton
                buttonVariant="square"
                reference={topic}
                source={currentPageType === PageType.Section ? "Section" : "HP Section Block"}
                type={FollowType.Topic}
              />
            )}
          </TopicContainer>
        );
      case withSection:
        return (
          <SectionContainer>
            <Section>{providers.sections()}</Section>
          </SectionContainer>
        );
      default:
        return null;
    }
  };

  return (
    <Container className={className} ref={ref}>
      <LeadingImage>
        <StyledEntityLink
          onClick={() => onClick?.(content.entityId, content.urlAlias)}
          query={plusQueryParameters}
          reference={content}
        >
          <StyledContentCoverImage
            reference={content}
            responsiveVariants={{
              desktopUp: "landscape250x99",
              mobileUp: "size540x360",
              tabletUp: "landscape250x99",
            }}
          />
          {withVideoOverlay &&
            providers.video?.durationOverlay({
              iconSize: "large",
              variant: "homepage",
            })}
        </StyledEntityLink>
      </LeadingImage>

      <Primary>
        <Header>
          {isPlusArticle && <PlusFlag pgtype={pgtype} />}
          {renderHeader()}
        </Header>
        <StyledEntityLink
          onClick={() => onClick?.(content.entityId, content.urlAlias)}
          query={{ ...plusQueryParameters, ...customQueryParameters }}
          reference={content}
        >
          <LinkHighlightArea>
            <Headline>
              {providers.headline({
                hideTooltip: true,
                preferSocialHeadline: true,
              })}
            </Headline>
            <Summary>
              {providers.summary({
                isTextOverflowEllipsis: true,
              })}
            </Summary>
          </LinkHighlightArea>
        </StyledEntityLink>
        {isSponsorArticle ? (
          providers.sponsorTag?.({ variant: "home" })
        ) : (
          <ActionBar>
            {providers.actionBar({
              commentVariant: "reverse",
              isBookmarkVisible: withBookmark ?? false,
              isCommentsVisible: withComment ?? false,
            })}
          </ActionBar>
        )}
      </Primary>
      <Rest>{children}</Rest>
    </Container>
  );
};

Component.displayName = "ContentItemSectionTopLeadPrimary";

export const ContentItemSectionTopLeadPrimary = withHighlightedHeadline(Component);
